import { SnackbarProgrammatic as Snackbar } from 'buefy'

import { BASE_PLAN } from '../../globals/premium.js'

/**
 * The reward track and Gamebot Premium state behind /profile/me.
 *
 * Kept separate from the `checkout` module because the two solve different
 * problems: `checkout` mounts an embedded Payment Element for one-off credit
 * and gold bundles, whereas Premium is a subscription handled entirely by
 * hosted Stripe pages — this module never touches Stripe.js, it just follows
 * a URL the server hands back.
 */

/**
 * Sends a signed-out visitor to log in, and back to where they were.
 *
 * Only for actions the user *initiated*. api() already returns null without a
 * session, which is right for the background fetches on this page — bouncing
 * someone to Discord because a reward track quietly failed to load would be
 * absurd. A claim is different: it is a button press with an obvious intent,
 * and doing nothing at all is the one response that explains nothing.
 *
 * The return path is the current URL rather than a hardcoded /profile/me, so
 * a claim from `/profile/<id>/stats` comes back to that same page.
 */
function requireLogin() {
    const back = window.location.pathname + window.location.search
    const url = new URL('/login', window.location.origin)
    url.searchParams.set('next', back)
    window.location = url.toString()
    return false
}

/**
 * Why a profile couldn't be shown, in our words.
 *
 * Keyed by the `reason` code the route sends, never by its prose. A request
 * that misses that route — a proxy in front of it, a deploy mid-flight, a
 * typo'd path — still answers with JSON carrying *some* `error`, and
 * rendering it verbatim once put an internal filesystem path on screen where
 * the privacy message belonged.
 */
const PROFILE_ERRORS = Object.freeze({
    hidden: "This user's profile is hidden.",
    not_found: "There's no Gamebot player with that ID.",
    rate_limited: "You're loading profiles too quickly. Try again in a minute.",
})

const PROFILE_ERROR_FALLBACK = 'Could not load that profile.'

/** Shared fetch wrapper: attaches auth, surfaces server errors, follows redirects. */
async function api(rootGetters, path, { method = 'GET', body } = {}) {
    const token = rootGetters.getToken
    // No optional chaining anywhere in web/src: vue-cli 4 / webpack 4
    // predate it and the build fails to parse it.
    const userID = rootGetters.getUser && rootGetters.getUser.id
    if (!token || !userID) return null

    const url = method === 'GET'
        ? `${path}${path.includes('?') ? '&' : '?'}userID=${userID}`
        : path

    const res = await fetch(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`,
        },
        ...(method !== 'GET' && { body: JSON.stringify({ userID, ...body }) }),
    })

    const json = await res.json().catch(() => ({}))

    if (!res.ok) {
        // A 401 means the Discord token expired out from under us — bouncing
        // to /login is more useful than an error the user can't act on.
        if (json.redirect) {
            window.location = json.redirect
            return null
        }
        const error = new Error(json.error || 'Something went wrong.')
        error.status = res.status
        throw error
    }

    return json
}

export default {
    namespaced: true,
    state: () => ({
        /**
         * Someone else's profile, when /profile/:id names a user other than
         * the signed-in one. Null on your own profile, where the page reads
         * the root store's `dbInfo` instead — that copy is authoritative and
         * updates as you claim rewards and change themes, which a fetched
         * snapshot would not.
         */
        viewed: null,
        viewedLoading: false,
        // Why, as copy this app owns — see PROFILE_ERRORS. "This user's
        // profile is hidden." is a state to render, not a failure to log.
        viewedError: null,
        // Levels with rewards this user can take right now.
        claimable: [],
        // Levels whose rewards are gated behind Premium. This is the upsell:
        // a free player sees exactly what has been piling up unclaimed.
        locked: [],
        // Whether /api/rewards/claimable has actually answered. Distinct from
        // "answered with nothing": without it, an unauthenticated or failed
        // load looks identical to "every past level already claimed", and the
        // track cheerfully renders checkmarks over rewards still owed.
        rewardsLoaded: false,
        premium: false,
        premiumUntil: 0,
        cancelAtPeriodEnd: false,
        /**
         * Whether there is any billing to reach — premium now, or a Stripe
         * customer from before. Drives whether the billing section renders at
         * all, and stays true for a `past_due` subscriber, whose `premium` is
         * false for the weeks Stripe spends retrying their card.
         */
        billing: false,
        /**
         * Whether Stripe's hosted portal is the right destination, which is
         * narrower: a Discord subscriber can hold a leftover Stripe customer
         * from a subscription they cancelled, and must not be offered a
         * button whose only outcome is an error.
         */
        stripeBilling: false,
        /** Which rails are currently paying — 'stripe', 'discord', 'manual'. */
        activeSources: [],
        // What Premium costs, read from Stripe rather than hardcoded so the
        // page can't drift from what the user is actually charged.
        price: null,
        // Per-plan Stripe prices, keyed by plan id. A plan missing from here
        // has no Stripe Price behind it and cannot be bought — which is what
        // the plan grid reads to decide between a buy button and "Coming
        // soon", rather than letting checkout fail at the last step.
        planPrices: {},
        loading: false,
        claiming: false,
        // Which level's button was pressed, so only that one spins. `claiming`
        // alone is a single flag every card binds, which put all of them into
        // a loading state at once and read as "everything is being claimed".
        // null while a claim-all runs — that's the one case where the whole
        // track really is in flight, and the Claim all button owns the spinner.
        claimingLevel: null,
        // The reward just claimed, for the celebration modal.
        lastClaim: null,
        // The achievement catalog (GET /api/achievements). Served rather than
        // mirrored into the SPA — one copy, no drift.
        achievements: [],
        // The daily quest pool (GET /api/quests). A user document stores only
        // { id, progress, completed }; descriptions and targets live here.
        // What this user may equip (GET /api/profile/customization). The
        // server decides; this is only what the picker renders.
        customization: {
            titles: [],
            themes: [],
            current: { title: null, theme: 'default', hideProfile: false },
        },
        customizationLoaded: false,
        saving: false,
        // id -> name for the games a stats key can refer to (GET /api/games).
        games: [],
        // The current season (GET /api/season), e.g.
        // { id: 8, season: 'Summer', year: 2026, name: 'Summer 2026' }.
        season: null,
        // Every season from the epoch to now, so the season picker can name
        // any id a user has history for. Shipped with the current season
        // rather than fetched per id — it's a handful of entries.
        seasonCatalog: [],
        questPool: [],
        questPoolLoaded: false,
        questResetMs: 24 * 60 * 60 * 1000,
    }),
    getters: {
        /** Achievement id/name → its definition, for resolving equipped titles. */
        achievementsById: state => state.achievements.reduce((map, achievement) => {
            map[achievement.id] = achievement
            // updateAchievements stores achievements by `name`, not by map
            // key, so a user's unlocked list can hold either. Index both.
            map[achievement.name] = achievement
            return map
        }, {}),
        // Flattened for display — the track renders per-reward, not per-level.
        lockedRewards: state => state.locked.flatMap(
            entry => entry.rewards.map(reward => ({ level: entry.level, ...reward }))
        ),
        claimableRewards: state => state.claimable.flatMap(
            entry => entry.rewards.map(reward => ({ level: entry.level, ...reward }))
        ),
        hasClaimable: state => state.claimable.length > 0,
        // Drives the headline on the upsell: "You're missing out on N rewards".
        lockedCount: (state, getters) => getters.lockedRewards.length,
    },
    mutations: {
        setRewards(state, { claimable, locked, premium }) {
            state.claimable = claimable || []
            state.locked = locked || []
            if (premium !== undefined) state.premium = premium
            state.rewardsLoaded = true
        },
        setPremium(state, status) {
            state.premium = status.premium === true
            state.premiumUntil = status.premiumUntil || 0
            state.cancelAtPeriodEnd = status.cancelAtPeriodEnd === true
            state.billing = status.billing === true
            state.stripeBilling = status.stripeBilling === true
            // `||`, not `??`: nullish coalescing is the same ES2020 vintage
            // as the optional chaining this file already warns about, and
            // webpack 4 cannot parse either. ESLint accepts both, so only the
            // build catches it.
            state.activeSources = status.activeSources || []
        },
        setPrice(state, price) {
            state.price = price

            const byId = {}
            const plans = (price && price.plans) || []
            for (const plan of plans) byId[plan.id] = plan

            // A server that predates multi-plan pricing answers with the base
            // plan's price and no `plans` at all. Without this the SPA would
            // read that as "nothing is on sale" and show Coming soon on the
            // one plan that has always been buyable — a regression that would
            // land the moment the frontend deployed ahead of the backend.
            if (price && price.unitAmount && !byId[BASE_PLAN.id]) {
                byId[BASE_PLAN.id] = {
                    id: BASE_PLAN.id,
                    priceId: price.id,
                    unitAmount: price.unitAmount,
                    currency: price.currency,
                    interval: price.interval,
                    name: price.name,
                }
            }

            state.planPrices = byId
        },
        setViewed(state, profile) {
            state.viewed = profile
            state.viewedError = null
        },
        setViewedError(state, message) {
            state.viewed = null
            state.viewedError = message
        },
        setViewedLoading(state, loading) { state.viewedLoading = loading },
        clearViewed(state) {
            state.viewed = null
            state.viewedError = null
            state.viewedLoading = false
        },
        setAchievements(state, achievements) { state.achievements = achievements },
        setGames(state, games) { state.games = games },
        setSeason(state, season) {
            state.season = season
            state.seasonCatalog = (season && season.seasons) || []
        },
        setQuestPool(state, { quests, resetMs }) {
            state.questPool = quests || []
            if (resetMs) state.questResetMs = resetMs
            state.questPoolLoaded = true
        },
        setCustomization(state, customization) {
            state.customization = customization
            state.customizationLoaded = true
        },
        setSaving(state, saving) { state.saving = saving },
        setLoading(state, loading) { state.loading = loading },
        setClaiming(state, claiming) { state.claiming = claiming },
        setClaimingLevel(state, level) { state.claimingLevel = level },
        setLastClaim(state, claim) { state.lastClaim = claim },
    },
    actions: {
        async fetchRewards({ commit, rootGetters }) {
            commit('setLoading', true)
            try {
                const json = await api(rootGetters, '/api/rewards/claimable')
                if (json) commit('setRewards', json)
            } catch (err) {
                console.error(err)
                Snackbar.open({ message: 'Could not load your rewards.', type: 'is-danger' })
            } finally {
                commit('setLoading', false)
            }
        },

        /**
         * Polls premium status until the webhook that grants it has landed.
         *
         * Stripe redirects the customer back the moment they pay, but premium
         * is granted by `checkout.session.completed` arriving on our webhook —
         * a different connection, usually a second or two behind. Checking
         * once and giving up meant a paying customer landed on a page that
         * still said "Get Premium", which is the worst possible moment to
         * look broken.
         */
        async awaitPremium({ dispatch, state }, { attempts = 6, delayMs = 1500 } = {}) {
            for (let attempt = 0; attempt < attempts; attempt++) {
                await dispatch('fetchPremiumStatus')
                if (state.premium === true) return true
                // Don't sleep after the final check — nothing would read it.
                if (attempt < attempts - 1) {
                    await new Promise(resolve => setTimeout(resolve, delayMs))
                }
            }
            return false
        },

        async fetchPremiumStatus({ commit, rootGetters }) {
            try {
                const json = await api(rootGetters, '/api/premium/status')
                if (json) commit('setPremium', json)
            } catch (err) {
                // Non-fatal: the profile still renders, just without the
                // subscription-specific chrome.
                console.error(err)
            }
        },

        // Public catalogs, no auth needed. Both are static for the life of a
        // deploy, so they're fetched once and cached in the store.
        async fetchAchievements({ state, commit }) {
            if (state.achievements.length) return
            try {
                const res = await fetch('/api/achievements')
                if (res.ok) commit('setAchievements', (await res.json()).achievements || [])
            } catch (err) {
                console.error(err)
            }
        },

        async fetchGames({ state, commit }) {
            if (state.games.length) return
            try {
                const res = await fetch('/api/games')
                if (res.ok) commit('setGames', (await res.json()).games || [])
            } catch (err) {
                console.error(err)
            }
        },

        async fetchSeason({ commit }) {
            try {
                const res = await fetch('/api/season')
                if (res.ok) commit('setSeason', await res.json())
            } catch (err) {
                console.error(err)
            }
        },

        async fetchQuestPool({ state, commit }) {
            if (state.questPool.length) return
            try {
                const res = await fetch('/api/quests')
                if (res.ok) commit('setQuestPool', await res.json())
            } catch (err) {
                console.error(err)
            }
        },

        // Unauthenticated on purpose — a logged-out visitor should still see
        // what Premium costs.
        async fetchPremiumPrice({ commit }) {
            try {
                const res = await fetch('/api/premium/price')
                if (res.ok) commit('setPrice', await res.json())
            } catch (err) {
                console.error(err)
            }
        },

        /**
         * Loads another user's profile for the read-only view.
         *
         * Deliberately not routed through api(): that helper returns null
         * when nobody is signed in, and this page is public — a logged-out
         * visitor following a link to someone's profile is the ordinary case.
         * The viewer's identity is sent when there is one, and is what lets a
         * user still see their own hidden profile.
         */
        async fetchProfile({ commit, rootGetters }, userID) {
            commit('setViewedLoading', true)
            try {
                const token = rootGetters.getToken
                const viewerID = rootGetters.getUser && rootGetters.getUser.id
                const query = token && viewerID ? `?viewerID=${encodeURIComponent(viewerID)}` : ''

                const res = await fetch(`/api/profile/${encodeURIComponent(userID)}${query}`, {
                    headers: token ? { authorization: `Bearer ${token}` } : {},
                })
                const json = await res.json().catch(() => ({}))

                if (!res.ok) {
                    commit('setViewedError', PROFILE_ERRORS[json.reason] || PROFILE_ERROR_FALLBACK)
                    return
                }
                commit('setViewed', json.profile)
            } catch (err) {
                console.error(err)
                commit('setViewedError', PROFILE_ERROR_FALLBACK)
            } finally {
                commit('setViewedLoading', false)
            }
        },

        async fetchCustomization({ commit, rootGetters }) {
            try {
                const json = await api(rootGetters, '/api/profile/customization')
                if (json) commit('setCustomization', json)
            } catch (err) {
                console.error(err)
                Snackbar.open({ message: 'Could not load your customization options.', type: 'is-danger' })
            }
        },

        /**
         * Saves a title and/or background. Only the keys passed are sent —
         * the endpoint treats an absent field as "leave this alone", so the
         * two pickers don't overwrite each other.
         */
        async customize({ commit, dispatch, rootGetters }, changes) {
            commit('setSaving', true)
            try {
                const json = await api(rootGetters, '/api/profile/customize', {
                    method: 'POST',
                    body: changes,
                })
                if (!json) return false

                // The applied theme and title are read from dbInfo, so refetch
                // rather than patching local state to match what we hope the
                // server did.
                await Promise.all([
                    dispatch('fetchDBInfo', null, { root: true }),
                    dispatch('fetchCustomization'),
                ])
                return true
            } catch (err) {
                // 403 means it's real but not theirs — worth saying plainly
                // rather than as a generic failure.
                Snackbar.open({ message: err.message, type: err.status === 403 ? 'is-warning' : 'is-danger' })
                return false
            } finally {
                commit('setSaving', false)
            }
        },

        async claim({ commit, dispatch, rootGetters }, level) {
            if (!rootGetters.getToken) return requireLogin()
            commit('setClaiming', true)
            commit('setClaimingLevel', level)
            try {
                const json = await api(rootGetters, '/api/rewards/claim', {
                    method: 'POST',
                    body: { level },
                })
                if (!json) return false

                commit('setLastClaim', { rewards: json.rewards, level })
                // Balances and the reward track both moved — refetch rather
                // than trying to patch state to match what the server did.
                await Promise.all([
                    dispatch('fetchRewards'),
                    dispatch('fetchDBInfo', null, { root: true }),
                ])
                return true
            } catch (err) {
                // 402 is the upsell, not a failure: this level's reward is
                // real, it just needs Premium.
                if (err.status === 402) {
                    Snackbar.open({
                        message: 'That reward needs Gamebot Premium.',
                        type: 'is-warning',
                    })
                } else {
                    Snackbar.open({ message: err.message, type: 'is-danger' })
                }
                return false
            } finally {
                commit('setClaiming', false)
                commit('setClaimingLevel', null)
            }
        },

        async claimAll({ commit, dispatch, rootGetters }) {
            if (!rootGetters.getToken) return requireLogin()
            commit('setClaiming', true)
            try {
                const json = await api(rootGetters, '/api/rewards/claimAll', { method: 'POST' })
                if (!json) return false

                commit('setLastClaim', { rewards: json.rewards })
                await Promise.all([
                    dispatch('fetchRewards'),
                    dispatch('fetchDBInfo', null, { root: true }),
                ])
                return true
            } catch (err) {
                Snackbar.open({ message: err.message, type: 'is-danger' })
                return false
            } finally {
                commit('setClaiming', false)
                commit('setClaimingLevel', null)
            }
        },

        // Both of these hand off to a Stripe-hosted page. The server mints the
        // URL; nothing here knows anything about cards.
        async subscribe({ rootGetters }, plan) {
            try {
                // The server maps the id to a Price through a fixed table, so
                // this is a choice between plans we put on sale, not a Price
                // the client gets to name.
                const json = await api(rootGetters, '/api/premium/checkout', {
                    method: 'POST',
                    body: plan ? { plan } : undefined,
                })
                if (json && json.url) window.location = json.url
            } catch (err) {
                Snackbar.open({ message: err.message, type: 'is-danger' })
            }
        },

        async manageBilling({ rootGetters }) {
            try {
                const json = await api(rootGetters, '/api/premium/portal', { method: 'POST' })
                if (json && json.url) window.location = json.url
            } catch (err) {
                Snackbar.open({ message: err.message, type: 'is-danger' })
            }
        },

        dismissClaim({ commit }) {
            commit('setLastClaim', null)
        },
    },
}
